import React, { useState, useMemo, useEffect } from 'react';
import DateRangePicker from '../finance/components/DateRangePicker';
import { getDatePresetLabel } from '../finance/data/mockData';
import { fmtMoney } from '../product/productCommerce';
import { productService } from '../../_services/product.service';
import { HeroInsight, Leaderboard, OpportunityList, StatTiles, BarList } from '../catalogue-data';
import '../gift-cards-v2/GiftCardsV2.css'; // .v2-sales-filterbar chrome for the date bar
import '../product/ProductV2.css';

// Services > Data — service intelligence from /service/reporting (real bookings).
// Top services, revenue-per-hour, rebook rate, stylist leaderboard + coaching.
// Upsell ("added at checkout") isn't tracked yet, so those widgets are omitted
// rather than faked. Money ledger lives in Finance (link top-right).

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M6 9l6 6 6-6" /></svg>
);
const ArrowRightIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);

const computePresetRange = (preset) => {
  const now = new Date();
  let startDate, endDate;
  switch (preset) {
    case 'today':       startDate = endDate = new Date(); break;
    case 'yesterday': { const d = new Date(); d.setDate(d.getDate() - 1); startDate = endDate = d; break; }
    case 'last7':       endDate = new Date(); startDate = new Date(); startDate.setDate(startDate.getDate() - 7); break;
    case 'last4weeks':  endDate = new Date(); startDate = new Date(); startDate.setDate(startDate.getDate() - 28); break;
    case 'last6months': endDate = new Date(); startDate = new Date(); startDate.setMonth(startDate.getMonth() - 6); break;
    case 'last12months':endDate = new Date(); startDate = new Date(); startDate.setFullYear(startDate.getFullYear() - 1); break;
    case 'mtd':         endDate = new Date(); startDate = new Date(now.getFullYear(), now.getMonth(), 1); break;
    case 'ytd':         endDate = new Date(); startDate = new Date(now.getFullYear(), 0, 1); break;
    case 'all':         endDate = new Date(); startDate = new Date(2020, 0, 1); break;
    default:            endDate = new Date(); startDate = new Date(); startDate.setFullYear(startDate.getFullYear() - 1);
  }
  return { startDate, endDate };
};

const ServiceData = ({ currency = '£', onOpenFinance }) => {
  const [dateFilter, setDateFilter] = useState('last12months');
  const initial = useMemo(() => computePresetRange('last12months'), []);
  const [dateStart, setDateStart] = useState(initial.startDate);
  const [dateEnd, setDateEnd] = useState(initial.endDate);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    let alive = true;
    productService.getServiceReport({ startDate: dateStart.toISOString(), endDate: dateEnd.toISOString() })
      .then((res) => {
        if (!alive) return;
        const data = res && res.data ? (res.data.responseData !== undefined ? res.data.responseData : res.data) : null;
        setReport(data || null);
      })
      .catch(() => { if (alive) setReport(null); });
    return () => { alive = false; };
  }, [dateStart, dateEnd]);

  const totals = (report && report.totals) || {};
  const topServices = (report && report.topServices) || [];
  const mostProfitable = (report && report.mostProfitable) || [];
  const leaderboard = (report && report.leaderboard) || [];
  const opps = (report && report.opportunities) || [];

  const rebookRate = totals.rebookRatePct || 0;
  const serviceRevenue = totals.serviceRevenue || 0;

  const dateLabel = dateFilter === null && dateStart && dateEnd
    ? `${dateStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${dateEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
    : getDatePresetLabel(dateFilter);

  const leaderRows = leaderboard.map((s, i) => ({
    id: s.staffId, name: s.name, value: fmtMoney(s.revenue, currency), valueNum: s.revenue,
    sub: `${s.bookings} booked`,
    badge: i === 0 ? 'Top earner' : undefined,
  }));
  const profRows = mostProfitable.slice(0, 5).map((p) => ({
    name: p.name, value: `${fmtMoney(p.revenuePerHour, currency)}/hr`, valueNum: p.revenuePerHour,
    sub: `${p.bookings} booked`,
  }));
  const topRows = topServices.slice(0, 5).map((s) => ({ name: s.name, value: fmtMoney(s.revenue, currency), valueNum: s.revenue, sub: `${s.bookings} booked` }));

  const opportunities = opps.map((o) => ({ id: o.key, icon: 'bulb', tone: 'teal', metric: o.title, advice: o.detail }));

  return (
    <div className="voucher-v2 cd-scope pv2-sales-page">
      <div className="v2-sales-filterbar">
        <div className="v2-sales-fb-group">
          <div className="v2-sales-fb-anchor">
            <button type="button" className="v2-sales-fb-btn" onClick={() => setDatePickerOpen((o) => !o)}>
              <span className="v2-sales-fb-label">Date range</span>
              <span className="v2-sales-fb-sep" />
              <span className="v2-sales-fb-val">{dateLabel}</span>
              <ChevronDownIcon />
            </button>
            <DateRangePicker
              isOpen={datePickerOpen}
              onClose={() => setDatePickerOpen(false)}
              startDate={dateStart}
              endDate={dateEnd}
              selectedPreset={dateFilter}
              onPresetChange={(p) => { setDateFilter(p); const r = computePresetRange(p); setDateStart(r.startDate); setDateEnd(r.endDate); }}
              onApply={(s, e) => { setDateStart(s); setDateEnd(e); setDateFilter(null); }}
            />
          </div>
        </div>
        {onOpenFinance && (
          <div className="v2-sales-fb-right">
            <button type="button" className="pv2-finance-link" onClick={onOpenFinance}>
              <span>Service transactions in Finance</span><ArrowRightIcon />
            </button>
          </div>
        )}
      </div>

      <div className="cd-stack">
        <HeroInsight
          eyebrow="Rebook rate"
          headline={`${rebookRate}% of your clients rebooked in this period`}
          sub="Booking the next appointment before a client leaves is the single biggest retention lever — small lifts here compound fast."
          metric={{ value: rebookRate, format: (n) => `${n}%`, caption: 'rebooked' }}
          tone="teal"
        />

        <StatTiles tiles={[
          { label: 'Bookings', value: String(totals.bookings || 0), hint: 'completed this period' },
          { label: 'Clients', value: String(totals.clients || 0), hint: 'unique clients seen' },
          { label: 'Avg service price', value: fmtMoney(totals.avgServicePrice || 0, currency), hint: 'per service line' },
          { label: 'Revenue / hour', value: `${fmtMoney(totals.revenuePerHour || 0, currency)}/hr`, hint: 'chair time is the scarce resource' },
        ]} />

        <Leaderboard title="Top stylists by service revenue" rows={leaderRows} />

        <div className="cd-grid-2">
          <BarList title="Most profitable per hour" rows={profRows} emptyText="No bookings in this period." />
          <BarList title="Top services" rows={topRows} emptyText="No bookings in this period." />
        </div>

        <OpportunityList title="Opportunities" items={opportunities} />
      </div>
    </div>
  );
};

export default ServiceData;
